import {
  createElement,
  useEffect,
  useState,
  Ref,
  ReactNode,
  CSSProperties,
  useRef,
  forwardRef,
} from 'react';
import { setAlpha } from '../../design-tokens';
import { Button } from '../Button';
import { BoxContentColor, DesignTokens, renderDesignTokens, useDesignTokens } from '../../theme';
import { BusyStateAnimationElement } from './BusyStateAnimationElement';
import { ButtonVariant } from '../Button';
import { Flow } from '../Layouts/Flow';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { setRef } from '../../util/ref-util';
import { NovaComponentProps } from '../../util/utility-types';
import cx from 'classnames';
import cn from './BusyStateIndicator.css.json';
import './BusyStateIndicator.css';

const useTextId = createUseHtmlId('BusyStateIndicator-text');
export type BusyStateIndicatorTokens = () => Partial<BoxContentColor>;

export interface BusyStateIndicatorProps
  extends NovaComponentProps<keyof ReturnType<typeof getBusyStateIndicatorDesignTokens>> {
  containerClassName?: string;
  containerStyle?: CSSProperties;
  overlayClassName?: string;
  overlayStyle?: CSSProperties;
  children?: ReactNode;
  active?: boolean;
  delay?: number;
  text?: string;
  buttonText?: string;
  onButtonPress?: () => void;
  onActive?: () => void;
  fillParentContainer?: boolean;
  busyStateAnimationRef?: Ref<HTMLDivElement>;
  ariaLabel?: string;
  /**
   * @deprecated use `theme` instead
   */
  tokens?: BusyStateIndicatorTokens;
  animationTokens?: () => { bg: string };
}

const calculateDelay = (delay?: number | null) => {
  if (delay === undefined || delay === null) return 300;
  return delay > 0 ? delay : 0;
};

export const BusyStateIndicator = forwardRef<HTMLDivElement, BusyStateIndicatorProps>(
  function BusyStateIndicator(props, ref) {
    const { text, buttonText, onButtonPress, active, children, testId } = props;
    const textId = useTextId();
    const delay = calculateDelay(props.delay);

    const [timerFinished, setTimerFinished] = useState<boolean>(false);
    const stateContainerRef = useRef<HTMLDivElement | null>(null);
    const childrenContainerRef = useRef<HTMLDivElement | null>(null);
    const busyStateAnimationRef = useRef<HTMLDivElement | null>(null);
    const beforeChildrenRef = useRef<HTMLSpanElement | null>(null);
    const prevFocusedElementRef = useRef<HTMLElement | null>(null);
    const ignoreOnFocusRef = useRef(false);

    const dt = useDesignTokens();

    const isBusyVisible = active && (timerFinished || delay === 0);

    const tokens = {
      ...getBusyStateIndicatorDesignTokens(dt, { tokens: props.tokens }),
      ...props.theme?.(dt),
    };

    useEffect(() => {
      if (!active) {
        setTimerFinished(false);
        return;
      }
      const timer = setTimeout(() => setTimerFinished(true), delay);
      return () => clearTimeout(timer);
    }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (isBusyVisible && childrenContainerRef.current?.contains(document.activeElement)) {
        // Store the previously focused element in the children so that it can be restored later
        prevFocusedElementRef.current = document.activeElement as HTMLElement;
        busyStateAnimationRef.current?.focus();
      } else if (!isBusyVisible && prevFocusedElementRef.current) {
        // Restore the previously focused element after the busy state indicator is removed.
        prevFocusedElementRef.current?.focus?.();
        prevFocusedElementRef.current = null;
      }
    }, [isBusyVisible]);

    const onActiveRef = useRef(props.onActive);
    onActiveRef.current = props.onActive;
    useEffect(() => {
      if (isBusyVisible) {
        onActiveRef.current?.();
      }
    }, [isBusyVisible]);

    if (!isBusyVisible && !stateContainerRef.current?.contains(document.activeElement)) {
      // Clear the previously focused element if the busy state indicator no longer has the focus
      prevFocusedElementRef.current = null;
    }

    const state = (
      <Flow
        className={cx(cn.state, props.className)}
        style={{ ...tokens, ...props.style }}
        data-testid={testId}
        ref={stateContainerRef}
        orientation="vertical"
      >
        <BusyStateAnimationElement
          ref={(element) => {
            setRef(props.busyStateAnimationRef, element);
            busyStateAnimationRef.current = element;
          }}
          ariaLabel={props.ariaLabel}
          ariaDescribedBy={text ? textId : undefined}
          tokens={props.animationTokens}
        />
        {text ? (
          <span className={cn.stateText} id={textId}>
            {text}
          </span>
        ) : null}
        {buttonText ? (
          <div className={cn.buttonContainer}>
            <Button variant={ButtonVariant.Small} onPress={onButtonPress} text={buttonText} />
          </div>
        ) : null}
      </Flow>
    );

    if (!children) {
      return isBusyVisible ? state : null;
    }

    return (
      <div
        className={cx(
          cn.container,
          props.fillParentContainer && cn.fillParentContainer,
          props.containerClassName
        )}
        style={{
          ...tokens,
          ...props.containerStyle,
        }}
        ref={ref}
      >
        {isBusyVisible ? (
          <span
            tabIndex={0}
            ref={beforeChildrenRef}
            onFocus={() => {
              if (!ignoreOnFocusRef.current) {
                busyStateAnimationRef.current?.focus();
              }
            }}
            data-autofocus-inside="true"
          />
        ) : null}
        <div className={cn.childrenContainer} ref={childrenContainerRef}>
          {children}
        </div>
        {isBusyVisible ? (
          <div
            className={cx(cn.overlay, props.overlayClassName)}
            style={props.overlayStyle}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && e.shiftKey && e.target === busyStateAnimationRef.current) {
                // Redirect the focus to the element before the children
                ignoreOnFocusRef.current = true;
                beforeChildrenRef.current?.focus();
                ignoreOnFocusRef.current = false;
              }
            }}
          >
            {state}
          </div>
        ) : null}
      </div>
    );
  }
);

export const getBusyStateIndicatorDesignTokens = (
  dt: DesignTokens,
  opts?: { tokens?: BusyStateIndicatorTokens }
) => {
  return {
    ...renderDesignTokens(
      {
        'font-size-m': dt.fontNova.size.m,
        'font-weight-thick': dt.fontNova.weight.thick,
        'overlay-bg': opts?.tokens?.().bg ?? setAlpha(dt.c.colorUiLight, 0.9),
        fg: opts?.tokens?.().fg ?? dt.c.colorInfo,
      },
      'busy-state'
    ),
    ...renderDesignTokens({ bg: dt.c.colorUiLight }, 'button-container'),
    ...(!dt.enableNovaTokens && getBusyStateIndicatorLegacyDesignTokens(dt, opts)),
  };
};

export const getBusyStateIndicatorLegacyDesignTokens = (
  dt: DesignTokens,
  opts?: { tokens?: BusyStateIndicatorTokens }
) => {
  let defaultBg = '';
  let defaultFg = '';
  switch (dt.themeVariant) {
    case 'dark':
      defaultBg = setAlpha(dt.c.p_2, 0.6);
      defaultFg = dt.c.white;
      break;
    case 'high-contrast':
      defaultBg = setAlpha(dt.c.black, 0.6);
      defaultFg = dt.c.white;
      break;
    default:
      defaultBg = setAlpha(dt.c.white, 0.8);
      defaultFg = dt.c.n_3;
  }

  return {
    ...renderDesignTokens(
      {
        'font-size-m': dt.font.size.m,
        'font-weight-thick': dt.font.weight.thick,
        'overlay-bg': opts?.tokens?.().bg ?? defaultBg,
        fg: opts?.tokens?.().fg ?? defaultFg,
      },
      'busy-state'
    ),
    ...renderDesignTokens({ bg: dt.backgrounds.panes.bg }, 'button-container'),
  };
};
